import { COMMUNITIES_ENDPOINTS } from "../../config/apiConfig";
import { createAuthAxios, handleApiError } from "../../utils/apiUtils";

/**
 * Service for community discovery operations (Phase 3)
 * Handles discovery homepage, search, browse by field/topic
 */
const discoveryService = {
  /**
   * Get discovery homepage with trending, popular, and recommended communities
   * @returns {Promise<Object>} Discovery homepage data
   */
  getDiscoveryHomepage: async () => {
    try {
      const api = createAuthAxios();
      const response = await api.get(COMMUNITIES_ENDPOINTS.DISCOVER_HOMEPAGE);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get popular communities
   * @param {string} sortBy - Sort by 'members' or 'activity' (default: members)
   * @param {number} limit - Number of communities (1-50, default: 10)
   * @returns {Promise<Object>} Popular communities data
   */
  getPopularCommunities: async (sortBy = "members", limit = 10) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("sortBy", sortBy);
      params.append("limit", limit.toString());

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.DISCOVER_POPULAR}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get recommended communities (personalized)
   * @param {number} limit - Number of communities (1-50, default: 10)
   * @returns {Promise<Object>} Recommended communities data
   */
  getRecommendedCommunities: async (limit = 10) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("limit", limit.toString());

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.DISCOVER_RECOMMENDED}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Search communities
   * @param {string} query - Search query (min 2 chars)
   * @param {number} limit - Number of results (1-50, default: 20)
   * @returns {Promise<Object>} Search results
   */
  searchCommunities: async (query, limit = 20) => {
    try {
      if (!query || query.trim().length < 2) {
        return { success: true, data: { communities: [] } };
      }

      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("query", query.trim());
      params.append("limit", limit.toString());

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.DISCOVER_SEARCH}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get communities by research field
   * @param {string} field - Research field name
   * @param {number} limit - Items per page (1-50, default: 20)
   * @param {number} page - Page number (default: 1)
   * @returns {Promise<Object>} Communities in field with pagination
   */
  getCommunitiesByField: async (field, limit = 20, page = 1) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      params.append("page", page.toString());

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.DISCOVER_BY_FIELD(field)}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get communities by topic
   * @param {string} topic - Topic slug
   * @param {number} limit - Items per page (1-50, default: 20)
   * @param {number} page - Page number (default: 1)
   * @returns {Promise<Object>} Communities with topic
   */
  getCommunitiesByTopic: async (topic, limit = 20, page = 1) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      params.append("page", page.toString());

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.DISCOVER_BY_TOPIC(topic)}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get all available topics
   * @param {number} limit - Number of topics (1-100, default: 50)
   * @returns {Promise<Object>} Topics list with community counts
   */
  getAllTopics: async (limit = 50) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("limit", limit.toString());

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.DISCOVER_TOPICS}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get all available research fields
   * @returns {Promise<Object>} Fields list with community counts
   */
  getAllFields: async () => {
    try {
      const api = createAuthAxios();
      const response = await api.get(COMMUNITIES_ENDPOINTS.DISCOVER_FIELDS);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },
};

export default discoveryService;
